/* Safe-range defaults and labels.
   Shared because two screens need the same numbers: Settings edits them and the
   Dashboard judges live readings against them. `/settings/ranges` returns `{}`
   until someone saves once, so both sides must fall back to the same values or
   the dashboard would flag readings Settings calls fine. */

/* `optimal` is a FLAT default, not stage-aware. The AI system prompt (app.py) already
   encodes per-growth-stage optimal bands (seedling/vegetative/flowering each differ),
   but nothing wires that table into Settings or the UI yet — that needs a schema
   change (ranges keyed by crop + stage, plus a rule for which stage governs a unit
   whose plants span several) and is deliberately not built here. These `optimal`
   values are the vegetative-stage numbers from that table, used farm-wide until
   stage-aware ranges exist. `warning` here is the "safe but not optimal" band.
   Backend copies: `optimal` -> TARGET_RANGES, warning/critical -> SAFE_RANGE_DEFAULTS
   (server/alerts.py, the WhatsApp tank-care rounds). Keep them in step. */
export const defaultRanges = {
  ph: {
    warning: { min: 5.8, max: 7.2 },
    optimal: { min: 5.8, max: 6.2 },   // research.md: 5.8-6.0 target, 5.5-6.5 range (26 Oct 2026)
    critical: { min: 5.0, max: 8.0 },
  },
  // TDS and EC are the farm's own working limits (set 2026-09-29), not a conversion of
  // each other: the EC warning band (0.5-2.0) is narrower than TDS/500 would give.
  tds: {
    warning: { min: 200, max: 1300 },
    optimal: { min: 700, max: 900 },   // = the EC target × 500
    critical: { min: 100, max: 1500 },
  },
  ec: {
    warning: { min: 0.5, max: 2.0 },
    optimal: { min: 1.4, max: 1.8 },   // Murano: 1.2-1.4 growing, 1.6-2.0 flowering, below standard varieties (research.md)
    critical: { min: 0.45, max: 2.1 },
  },
  water_temp: {
    warning: { min: 18, max: 26 },
    optimal: { min: 18, max: 24 },
    critical: { min: 15, max: 30 },
  },
  room_temp: { warning: { min: 18, max: 30 }, critical: { min: 15, max: 35 } },
  humidity: { warning: { min: 40, max: 80 }, critical: { min: 30, max: 90 } },
  co2: { warning: { min: 300, max: 1200 }, critical: { min: 250, max: 1500 } },
};

/* The ranges every page should use: what Settings saved, band by band over these
   defaults. Band by band (not sensor by sensor) so a sensor saved once still picks up
   a band it never stored, and a code change to a default still reaches any band the
   operator has not overridden.

   `optimal` is not editable in Settings, so it always comes from the code - clamped
   inside the effective warning band, so narrowing "safe" on the Settings page can
   never leave a "target" that is itself unsafe. A copy of `optimal` inside an older
   save is ignored for the same reason. */
export function resolveRanges(saved) {
  const out = {};
  Object.keys({ ...defaultRanges, ...(saved || {}) }).forEach(key => {
    const d = defaultRanges[key] || {};
    const s = saved?.[key] || {};
    const r = {};
    ['warning', 'critical'].forEach(band => {
      if (d[band] || s[band]) r[band] = { ...d[band], ...s[band] };
    });
    if (d.optimal) {
      const w = r.warning;
      r.optimal = w
        ? {
          min: Math.min(Math.max(d.optimal.min, w.min), w.max),
          max: Math.max(Math.min(d.optimal.max, w.max), w.min),
        }
        : d.optimal;
    }
    out[key] = r;
  });
  return out;
}

/* What Settings stores: only the bands it edits. */
export function rangesToSave(ranges) {
  const out = {};
  Object.entries(ranges || {}).forEach(([key, r]) => {
    out[key] = { warning: r.warning, critical: r.critical };
  });
  return out;
}

export const rangeLabels = {
  ph: { icon: '◎', name: 'pH Level', unit: '' },
  tds: { icon: '◆', name: 'TDS', unit: 'ppm' },
  ec: { icon: '◆', name: 'EC', unit: 'mS/cm' },
  water_temp: { icon: '◈', name: 'Water Temp', unit: '°C' },
  room_temp: { icon: '◇', name: 'Room Temp', unit: '°C' },
  humidity: { icon: '◍', name: 'Humidity', unit: '%' },
  co2: { icon: '✧', name: 'CO2', unit: 'ppm' },
};

/* 'critical' | 'warning' | null for a value against one sensor's range block. */
export function classifyReading(sensorKey, value, ranges = defaultRanges) {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  const range = ranges?.[sensorKey] || defaultRanges[sensorKey];
  if (!range) return null;
  const { critical, warning } = range;
  if (critical && (value < critical.min || value > critical.max)) return 'critical';
  if (warning && (value < warning.min || value > warning.max)) return 'warning';
  return null;
}

/* Colour for a reading tier or unit status. Shared by the Dashboard and Hydro Units
   so the same state never gets two colours. */
export function statusColor(theme, status) {
  if (status === 'critical') return theme.colors.critical;
  // `warning` in the theme is yellow; the orange temperature token reads as the
  // middle step between red and yellow, which is what these three tiers need.
  if (status === 'warning') return theme.colors.temperature;
  if (status === 'notice') return theme.colors.warning;
  if (status === 'maintenance') return theme.colors.warningHover;
  if (status === 'offline') return theme.colors.offline;
  return theme.colors.success;
}

/* Which side of the safe band a reading fell off — the direction is what tells a
   grower what to do about it. */
export function breachDirection(sensorKey, value, ranges) {
  const range = ranges?.[sensorKey] || defaultRanges[sensorKey];
  const band = range?.warning || range?.critical;
  if (!band) return 'outside';
  return value > band.max ? 'above' : 'below';
}
